import { supabase } from '@/shared/supabase/client'
import { resolveAuthUserAfterRefresh } from '@/shared/supabase/resolveAuthUserAfterRefresh'
import { isScreenshotFixtureMode } from '@/shared/lib/screenshotMode'
import type { SharedExamByTokenRecord } from '@/shared/repositories/exams/sharedExamByTokenRecord'
import { fixtureSharedExamByToken } from '@/shared/screenshotSeed/fixtureSharedExamByToken'
import { screenshotFixtureExamResults } from '@/shared/screenshotSeed/synapScreenshotFixtures'
import { toNumericValue } from '@/shared/lib/exam'
import { normalizeIdentity } from '@/shared/lib/normalize'
import { resolveExamTypeToMain } from '@/shared/repositories/examTypeAliases/examTypeAliasesRepository'
import { normalizePatientSex } from '@/shared/lib/patient'
import { AppError } from '@/shared/lib/appError'
import { toRepositoryError } from '@/shared/repositories/common/repositoryError'
import { patientsRepository } from '@/shared/repositories/patients/patientsRepository'
import type { Database } from '@/shared/supabase/database.types'

type PatientRow = Pick<
  Database['public']['Tables']['patients']['Row'],
  'id' | 'full_name' | 'normalized_name' | 'document_number' | 'email' | 'birth_date' | 'sex' | 'city' | 'created_at'
>

type ExamRow = Pick<
  Database['public']['Tables']['exam_results']['Row'],
  'id' | 'numeric_value' | 'text_value' | 'unit'
>

export interface ImportExamPayload {
  tipo_exame: string
  resultado: string | number
  data_exame?: string
  laboratorio?: string
  observacoes?: string
  unidade?: string
  ref_min?: number
  ref_max?: number
  limiar_alerta_percent?: number
}

export interface ImportExamPatientPayload {
  nome: string
  documento?: string
  email?: string
  data_nascimento?: string
  sexo?: string
  cidade?: string
}

export interface ImportExamBatchEntry {
  paciente: ImportExamPatientPayload
  exames: ImportExamPayload[]
}

export interface ImportExamsResult {
  examsImported: number
  examsAlreadyExisted: number
  examsInvalid: number
  patientsCreated: number
  patientsMatched: number
  patientsAmbiguous: number
  errors?: string[]
}

export interface ApiExamResult {
  id: string
  patientId: string
  examTypeId: string
  examName: string
  normalizedExamName: string
  date: string
  value: string | number
  numericValue?: number
  textValue?: string
  unit?: string
  laboratory?: string
  notes?: string
  refMin?: number
  refMax?: number
  warningThresholdPercent?: number
  createdAt: string
}

export interface CreateExamResultPayload {
  patientId: string
  examName: string
  date: string
  value: string | number
  unit?: string
  laboratory?: string
  notes?: string
  refMin?: number
  refMax?: number
  warningThresholdPercent?: number
}

export interface ExamShareLinkRecord {
  id: string
  examResultId: string
  grantedByUserId: string
  expiresAt: string
  revokedAt?: string
  createdAt: string
  lastAccessedAt?: string
}

export type { SharedExamByTokenRecord } from '@/shared/repositories/exams/sharedExamByTokenRecord'

const getCurrentUserId = async (): Promise<string> => {
  const { data: sessionData } = await supabase.auth.getSession()
  if (sessionData?.session?.user?.id) return sessionData.session.user.id

  const tryGetUser = async (): Promise<string> => {
    const { data, error } = await supabase.auth.getUser()
    if (error) throw error
    if (!data.user) throw new Error('Usuário não autenticado.')
    return data.user.id
  }

  try {
    return await tryGetUser()
  } catch (firstErr) {
    const msg = (firstErr instanceof Error ? firstErr.message : String(firstErr ?? '')).toLowerCase()
    const isSessionError =
      msg.includes('session') ||
      msg.includes('refresh') ||
      msg.includes('missing') ||
      msg.includes('auth') ||
      msg.includes('invalid') ||
      msg.includes('jwt') ||
      msg.includes('token') ||
      msg.includes('expired') ||
      msg.includes('autenticado')
    if (isSessionError) {
      const { error: refreshErr } = await supabase.auth.refreshSession()
      if (!refreshErr) {
        return await resolveAuthUserAfterRefresh(
          tryGetUser,
          'Sessão expirada ou inválida. Faça login novamente.',
        )
      }
    }
    throw toRepositoryError(
      firstErr,
      'Sessão expirada ou inválida. Faça login novamente.',
      401,
    )
  }
}

const toApiExamResult = (row: {
  id: string
  patient_id: string
  exam_name: string
  normalized_exam_name: string
  date: string
  numeric_value: number | null
  text_value: string | null
  unit: string | null
  laboratory: string | null
  notes: string | null
  ref_min: number | null
  ref_max: number | null
  warning_threshold_percent: number | null
  created_at: string
}): ApiExamResult => {
  const numericValue = row.numeric_value ?? undefined
  const textValue = row.text_value ?? undefined
  const value: string | number = numericValue ?? textValue ?? ''

  return {
    id: row.id,
    patientId: row.patient_id,
    examTypeId: row.normalized_exam_name,
    examName: row.exam_name,
    normalizedExamName: row.normalized_exam_name,
    date: row.date,
    value,
    numericValue,
    textValue,
    unit: row.unit ?? undefined,
    laboratory: row.laboratory ?? undefined,
    notes: row.notes ?? undefined,
    refMin: row.ref_min ?? undefined,
    refMax: row.ref_max ?? undefined,
    warningThresholdPercent: row.warning_threshold_percent ?? undefined,
    createdAt: row.created_at,
  }
}

const toExamValueColumns = (value: string | number): { numericValue: number | null; textValue: string | null } => {
  if (typeof value === 'number') {
    return {
      numericValue: Number.isFinite(value) ? value : null,
      textValue: Number.isFinite(value) ? null : String(value),
    }
  }

  const cleanText = value.trim()
  const numericCandidate = toNumericValue(cleanText)
  if (numericCandidate !== null) {
    return {
      numericValue: numericCandidate,
      textValue: null,
    }
  }

  return {
    numericValue: null,
    textValue: cleanText || null,
  }
}

const toExamShareLinkRecord = (row: {
  id: string
  exam_result_id: string
  granted_by_user_id: string
  expires_at: string
  revoked_at: string | null
  created_at: string
  last_accessed_at: string | null
}): ExamShareLinkRecord => ({
  id: row.id,
  examResultId: row.exam_result_id,
  grantedByUserId: row.granted_by_user_id,
  expiresAt: row.expires_at,
  revokedAt: row.revoked_at ?? undefined,
  createdAt: row.created_at,
  lastAccessedAt: row.last_accessed_at ?? undefined,
})

const buildExamShareUrl = (token: string): string => {
  const encodedToken = encodeURIComponent(token.trim())
  if (typeof window === 'undefined') {
    return `/exam-share/${encodedToken}`
  }
  return `${window.location.origin}/exam-share/${encodedToken}`
}

const parseNumberOrNull = (value: unknown): number | null => {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null
  }
  if (typeof value === 'string') {
    const normalized = value.trim().replace(',', '.')
    if (!normalized) return null
    const parsed = Number(normalized)
    return Number.isFinite(parsed) ? parsed : null
  }
  return null
}

const normalizeOptionalText = (value: unknown): string | undefined => {
  if (typeof value !== 'string') return undefined
  const cleaned = value.trim()
  return cleaned || undefined
}

const normalizeEmail = (value: unknown): string | undefined => {
  const cleaned = normalizeOptionalText(value)?.toLowerCase()
  return cleaned || undefined
}

const collapseSpaces = (value: string): string => value.replace(/\s+/g, ' ').trim()

const normalizePatientNameLoose = (value: unknown): string =>
  collapseSpaces(normalizeIdentity(value).replace(/[^a-z0-9\s]/g, ' '))

const buildPatientNameMatchCandidates = (value: unknown): string[] => {
  const base = collapseSpaces(normalizeIdentity(value))
  const withoutPunctuation = normalizePatientNameLoose(value)
  const withSingleLetterDots = collapseSpaces(withoutPunctuation.replace(/\b([a-z])\b/g, '$1.'))

  return [...new Set([base, withoutPunctuation, withSingleLetterDots].filter(Boolean))]
}

const normalizeBirthDate = (value: unknown): string | undefined => {
  if (typeof value !== 'string') return undefined
  const trimmed = value.trim()
  if (!trimmed) return undefined

  const isoDateOnly = /^\d{4}-\d{2}-\d{2}$/
  if (isoDateOnly.test(trimmed)) return trimmed

  const brDate = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(trimmed)
  if (brDate) {
    const [, day, month, year] = brDate
    return `${year}-${month}-${day}`
  }

  const parsed = new Date(trimmed)
  if (Number.isNaN(parsed.getTime())) return undefined
  return parsed.toISOString().slice(0, 10)
}

const normalizeExamDate = (value: unknown): string | null => {
  if (typeof value !== 'string' || !value.trim()) {
    return new Date().toISOString()
  }

  const trimmed = value.trim()
  const isoDateOnly = /^\d{4}-\d{2}-\d{2}$/
  if (isoDateOnly.test(trimmed)) {
    return `${trimmed}T00:00:00.000Z`
  }

  const brDate = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(trimmed)
  if (brDate) {
    const [, day, month, year] = brDate
    return `${year}-${month}-${day}T00:00:00.000Z`
  }

  const parsed = new Date(trimmed)
  if (Number.isNaN(parsed.getTime())) return null
  return parsed.toISOString()
}

const buildExamValueSignature = ({
  numericValue,
  textValue,
  unit,
}: {
  numericValue: number | null
  textValue: string | null
  unit: string | null
}): string => {
  const normalizedUnit = normalizeIdentity(unit ?? '')
  if (numericValue !== null) {
    return `n:${numericValue}|u:${normalizedUnit}`
  }
  return `t:${normalizeIdentity(textValue ?? '')}|u:${normalizedUnit}`
}

const toPatientRowFromRepo = (patient: {
  id: string
  name: string
  normalizedName: string
  documentNumber?: string
  email?: string
  birthDate?: string
  sex?: string
  city?: string
  createdAt: string
}): PatientRow => ({
  id: patient.id,
  full_name: patient.name,
  normalized_name: patient.normalizedName,
  document_number: patient.documentNumber ?? null,
  email: patient.email ?? null,
  birth_date: patient.birthDate ?? null,
  sex: normalizePatientSex(patient.sex) ?? null,
  city: patient.city ?? null,
  created_at: patient.createdAt,
})

const getPatientLookupKey = (payload: ImportExamPatientPayload): string =>
  [
    normalizeOptionalText(payload.documento)?.toLowerCase() ?? '',
    normalizeEmail(payload.email) ?? '',
    normalizePatientNameLoose(payload.nome),
    normalizeBirthDate(payload.data_nascimento) ?? '',
  ].join('|')

type PatientResolution =
  | { kind: 'resolved'; patient: PatientRow; created: boolean }
  | { kind: 'ambiguous'; message: string }

const PATIENT_SELECT_COLUMNS =
  'id, full_name, normalized_name, document_number, email, birth_date, sex, city, created_at'

const createPatientsImportQuery = (ownerUserId: string) =>
  supabase
    .from('patients')
    .select(PATIENT_SELECT_COLUMNS)
    .eq('owner_user_id', ownerUserId)

type PatientsImportQuery = ReturnType<typeof createPatientsImportQuery>

const resolvePatientForImport = async (
  payload: ImportExamPatientPayload,
  ownerUserId: string,
): Promise<PatientResolution> => {
  const normalizedNameCandidates = buildPatientNameMatchCandidates(payload.nome)
  const normalizedName = normalizedNameCandidates[0]
  if (!normalizedName) {
    return { kind: 'ambiguous', message: 'nome do paciente é obrigatório.' }
  }

  const documentNumber = normalizeOptionalText(payload.documento)
  const email = normalizeEmail(payload.email)
  const birthDate = normalizeBirthDate(payload.data_nascimento)
  const sex = normalizePatientSex(payload.sexo)
  const city = normalizeOptionalText(payload.cidade)

  const findByQuery = async (
    queryBuilder: (query: PatientsImportQuery) => PatientsImportQuery,
  ) => {
    const baseQuery = createPatientsImportQuery(ownerUserId)
    const { data, error } = await queryBuilder(baseQuery)
      .order('created_at', { ascending: false })
      .limit(3)

    if (error) {
      throw toRepositoryError(error, 'Não foi possível localizar paciente para importação.')
    }

    return (data ?? []) as PatientRow[]
  }

  const chooseLatestMatch = (rows: PatientRow[]): PatientResolution | null => {
    if (rows.length === 0) return null
    return { kind: 'resolved', patient: rows[0], created: false }
  }

  if (documentNumber) {
    const byDocument = await findByQuery((query) => query.eq('document_number', documentNumber))
    const chosenByDocument = chooseLatestMatch(byDocument)
    if (chosenByDocument) {
      if (chosenByDocument.kind === 'resolved') {
        const updates: Database['public']['Tables']['patients']['Update'] = {}
        if (!chosenByDocument.patient.email && email) updates.email = email
        if (!chosenByDocument.patient.birth_date && birthDate) updates.birth_date = birthDate
        if (!chosenByDocument.patient.sex && sex) updates.sex = sex
        if (!chosenByDocument.patient.city && city) updates.city = city
        if (Object.keys(updates).length > 0) {
          const { data: updated, error } = await supabase
            .from('patients')
            .update(updates)
            .eq('id', chosenByDocument.patient.id)
            .select(PATIENT_SELECT_COLUMNS)
            .single()
          if (error) {
            throw toRepositoryError(error, 'Não foi possível atualizar paciente existente na importação.')
          }
          return { kind: 'resolved', patient: updated as PatientRow, created: false }
        }
      }
      return chosenByDocument
    }
  }

  if (email) {
    const byEmail = await findByQuery((query) => query.ilike('email', email))
    const chosenByEmail = chooseLatestMatch(byEmail)
    if (chosenByEmail) {
      if (chosenByEmail.kind === 'resolved') {
        const updates: Database['public']['Tables']['patients']['Update'] = {}
        if (!chosenByEmail.patient.document_number && documentNumber) {
          updates.document_number = documentNumber
        }
        if (!chosenByEmail.patient.birth_date && birthDate) updates.birth_date = birthDate
        if (!chosenByEmail.patient.sex && sex) updates.sex = sex
        if (!chosenByEmail.patient.city && city) updates.city = city
        if (Object.keys(updates).length > 0) {
          const { data: updated, error } = await supabase
            .from('patients')
            .update(updates)
            .eq('id', chosenByEmail.patient.id)
            .select(PATIENT_SELECT_COLUMNS)
            .single()
          if (error) {
            throw toRepositoryError(error, 'Não foi possível atualizar paciente existente na importação.')
          }
          return { kind: 'resolved', patient: updated as PatientRow, created: false }
        }
      }
      return chosenByEmail
    }
  }

  if (birthDate) {
    const byNameAndBirth = await findByQuery((query) =>
      query.eq('normalized_name', normalizedName).eq('birth_date', birthDate),
    )
    const chosenByNameAndBirth = chooseLatestMatch(byNameAndBirth)
    if (chosenByNameAndBirth) {
      if (chosenByNameAndBirth.kind === 'resolved') {
        const updates: Database['public']['Tables']['patients']['Update'] = {}
        if (!chosenByNameAndBirth.patient.document_number && documentNumber) {
          updates.document_number = documentNumber
        }
        if (!chosenByNameAndBirth.patient.email && email) updates.email = email
        if (!chosenByNameAndBirth.patient.sex && sex) updates.sex = sex
        if (!chosenByNameAndBirth.patient.city && city) updates.city = city
        if (Object.keys(updates).length > 0) {
          const { data: updated, error } = await supabase
            .from('patients')
            .update(updates)
            .eq('id', chosenByNameAndBirth.patient.id)
            .select(PATIENT_SELECT_COLUMNS)
            .single()
          if (error) {
            throw toRepositoryError(error, 'Não foi possível atualizar paciente existente na importação.')
          }
          return { kind: 'resolved', patient: updated as PatientRow, created: false }
        }
      }
      return chosenByNameAndBirth
    }
  }

  for (const candidateName of normalizedNameCandidates) {
    const byName = await findByQuery((query) => query.eq('normalized_name', candidateName))
    const chosenByName = chooseLatestMatch(byName)
    if (!chosenByName) {
      continue
    }

    if (chosenByName.kind === 'resolved') {
      const updates: Database['public']['Tables']['patients']['Update'] = {}
      if (!chosenByName.patient.document_number && documentNumber) updates.document_number = documentNumber
      if (!chosenByName.patient.email && email) updates.email = email
      if (!chosenByName.patient.birth_date && birthDate) updates.birth_date = birthDate
      if (!chosenByName.patient.sex && sex) updates.sex = sex
      if (!chosenByName.patient.city && city) updates.city = city
      if (Object.keys(updates).length > 0) {
        const { data: updated, error } = await supabase
          .from('patients')
          .update(updates)
          .eq('id', chosenByName.patient.id)
          .select(PATIENT_SELECT_COLUMNS)
          .single()
        if (error) {
          throw toRepositoryError(error, 'Não foi possível atualizar paciente existente na importação.')
        }
        return { kind: 'resolved', patient: updated as PatientRow, created: false }
      }
    }

    return chosenByName
  }

  const created = await patientsRepository.upsert({
    name: payload.nome,
    documentNumber,
    email,
    birthDate,
    sex,
    city,
  })

  return {
    kind: 'resolved',
    patient: toPatientRowFromRepo(created.patient),
    created: created.created,
  }
}

export const examsRepository = {
  listResults: async (patientId?: string): Promise<{ results: ApiExamResult[] }> => {
    if (isScreenshotFixtureMode()) {
      return { results: screenshotFixtureExamResults(patientId) }
    }
    try {
      // Garante sessão ativa antes de consultar tabelas protegidas por RLS.
      await getCurrentUserId()

      let query = supabase
        .from('exam_results')
        .select('*')
        .order('date', { ascending: false })
        .order('created_at', { ascending: false })

      if (patientId) {
        query = query.eq('patient_id', patientId)
      }

      const { data, error } = await query
      if (error) {
        throw toRepositoryError(error, 'Não foi possível listar exames.')
      }

      return {
        results: (data ?? []).map(toApiExamResult),
      }
    } catch (error) {
      throw toRepositoryError(error, 'Não foi possível listar exames.')
    }
  },

  createResult: async (
    payload: CreateExamResultPayload,
  ): Promise<{ result: ApiExamResult }> => {
    try {
      const userId = await getCurrentUserId()
      const normalizedDate = new Date(payload.date)
      const date = Number.isNaN(normalizedDate.getTime())
        ? new Date().toISOString()
        : normalizedDate.toISOString()
      const { numericValue, textValue } = toExamValueColumns(payload.value)
      if (numericValue === null && !textValue) {
        throw new AppError('Resultado do exame é obrigatório.', {
          status: 422,
          code: 'VALIDATION',
        })
      }

      const trimmedName = payload.examName.trim()
      const normalizedName = normalizeIdentity(trimmedName)
      let canonicalDisplay = trimmedName
      try {
        const resolved = await resolveExamTypeToMain(userId, normalizedName)
        if (resolved?.mainDisplay) canonicalDisplay = resolved.mainDisplay
      } catch {
        // Mantém nome original
      }

      const { data, error } = await supabase
        .from('exam_results')
        .insert({
          patient_id: payload.patientId,
          created_by_user_id: userId,
          exam_name: canonicalDisplay,
          date,
          numeric_value: numericValue,
          text_value: textValue,
          unit: payload.unit ?? null,
          laboratory: payload.laboratory ?? null,
          notes: payload.notes ?? null,
          ref_min: payload.refMin ?? null,
          ref_max: payload.refMax ?? null,
          warning_threshold_percent: payload.warningThresholdPercent ?? null,
          visibility: 'care_team',
        })
        .select('*')
        .single()

      if (error) {
        throw toRepositoryError(error, 'Não foi possível criar resultado de exame.')
      }

      return { result: toApiExamResult(data) }
    } catch (error) {
      throw toRepositoryError(error, 'Não foi possível criar resultado de exame.')
    }
  },

  deleteResults: async (ids: string[]): Promise<void> => {
    if (!ids.length) return
    try {
      await getCurrentUserId()
      const { error } = await supabase.from('exam_results').delete().in('id', ids)
      if (error) {
        throw toRepositoryError(error, 'Não foi possível excluir os resultados de exame.')
      }
    } catch (error) {
      throw toRepositoryError(error, 'Não foi possível excluir os resultados de exame.')
    }
  },

  createExamShareLink: async (
    examResultId: string,
    expiresInHours = 72,
  ): Promise<{ share: ExamShareLinkRecord; token: string; url: string }> => {
    try {
      const normalizedExamResultId = examResultId.trim()
      if (!normalizedExamResultId) {
        throw new AppError('Exame inválido para compartilhamento.', {
          status: 422,
          code: 'VALIDATION',
        })
      }

      const userId = await getCurrentUserId()
      const { data, error } = await supabase.rpc('create_exam_share_link', {
        p_exam_result_id: normalizedExamResultId,
        p_expires_in_hours: expiresInHours,
      })

      if (error) {
        throw toRepositoryError(error, 'Não foi possível gerar link de compartilhamento.')
      }

      const firstRow = Array.isArray(data) ? data[0] : null
      if (!firstRow || !firstRow.share_id || !firstRow.token) {
        throw new AppError('Não foi possível gerar link de compartilhamento.', {
          status: 500,
          code: 'UNKNOWN',
        })
      }

      const share: ExamShareLinkRecord = {
        id: firstRow.share_id,
        examResultId: normalizedExamResultId,
        grantedByUserId: userId,
        expiresAt: firstRow.expires_at,
        createdAt: firstRow.created_at,
      }

      return {
        share,
        token: firstRow.token,
        url: buildExamShareUrl(firstRow.token),
      }
    } catch (error) {
      throw toRepositoryError(error, 'Não foi possível gerar link de compartilhamento.')
    }
  },

  listExamShareLinks: async (
    examResultId: string,
  ): Promise<{ links: ExamShareLinkRecord[] }> => {
    try {
      const normalizedExamResultId = examResultId.trim()
      if (!normalizedExamResultId) {
        return { links: [] }
      }

      const { data, error } = await supabase
        .from('exam_share_links')
        .select('*')
        .eq('exam_result_id', normalizedExamResultId)
        .order('created_at', { ascending: false })

      if (error) {
        throw toRepositoryError(error, 'Não foi possível listar links de compartilhamento.')
      }

      return {
        links: (data ?? []).map(toExamShareLinkRecord),
      }
    } catch (error) {
      throw toRepositoryError(error, 'Não foi possível listar links de compartilhamento.')
    }
  },

  revokeExamShareLink: async (shareId: string): Promise<{ revoked: boolean }> => {
    try {
      const normalizedShareId = shareId.trim()
      if (!normalizedShareId) {
        return { revoked: false }
      }

      const { data, error } = await supabase.rpc('revoke_exam_share_link', {
        p_share_id: normalizedShareId,
      })

      if (error) {
        throw toRepositoryError(error, 'Não foi possível revogar link de compartilhamento.')
      }

      return {
        revoked: Boolean(data),
      }
    } catch (error) {
      throw toRepositoryError(error, 'Não foi possível revogar link de compartilhamento.')
    }
  },

  getSharedExamByToken: async (
    token: string,
  ): Promise<{ sharedExam: SharedExamByTokenRecord | null }> => {
    try {
      const normalizedToken = token.trim()
      if (!normalizedToken) {
        return { sharedExam: null }
      }

      if (isScreenshotFixtureMode()) {
        const sharedExam = fixtureSharedExamByToken(normalizedToken)
        if (sharedExam) return { sharedExam }
      }

      const { data, error } = await supabase.rpc('get_shared_exam_by_token', {
        p_token: normalizedToken,
      })

      if (error) {
        throw toRepositoryError(error, 'Não foi possível validar link compartilhado.')
      }

      const firstRow = Array.isArray(data) ? data[0] : null
      if (!firstRow) {
        return { sharedExam: null }
      }

      const numericValue = firstRow.value_numeric ?? undefined
      const textValue = firstRow.value_text ?? undefined
      const value = numericValue ?? textValue ?? ''

      return {
        sharedExam: {
          shareId: firstRow.share_id,
          examResultId: firstRow.exam_result_id,
          examName: firstRow.exam_name,
          examDate: firstRow.exam_date,
          value,
          numericValue,
          textValue,
          unit: firstRow.unit ?? undefined,
          laboratory: firstRow.laboratory ?? undefined,
          notes: firstRow.notes ?? undefined,
          refMin: firstRow.ref_min ?? undefined,
          refMax: firstRow.ref_max ?? undefined,
          warningThresholdPercent: firstRow.warning_threshold_percent ?? undefined,
          patientName: firstRow.patient_name,
          expiresAt: firstRow.expires_at,
        },
      }
    } catch (error) {
      throw toRepositoryError(error, 'Não foi possível validar link compartilhado.')
    }
  },

  importExams: async (entries: ImportExamBatchEntry[]): Promise<ImportExamsResult> => {
    try {
      if (!Array.isArray(entries) || entries.length === 0) {
        throw new AppError('A importação deve conter ao menos um paciente com exames.', {
          status: 422,
          code: 'VALIDATION',
        })
      }

      const userId = await getCurrentUserId()
      const errors: string[] = []
      const patientCache = new Map<string, PatientResolution>()
      const countedPatientIds = new Set<string>()
      const examSignaturesByKey = new Map<string, Set<string>>()

      let examsImported = 0
      let examsAlreadyExisted = 0
      let examsInvalid = 0
      let patientsCreated = 0
      let patientsMatched = 0
      let patientsAmbiguous = 0

      for (let patientIndex = 0; patientIndex < entries.length; patientIndex += 1) {
        const batchEntry = entries[patientIndex]
        const patientLine = `Paciente ${patientIndex + 1}`
        if (!batchEntry || typeof batchEntry !== 'object') {
          patientsAmbiguous += 1
          errors.push(`${patientLine}: item inválido.`)
          continue
        }

        if (!Array.isArray(batchEntry.exames) || batchEntry.exames.length === 0) {
          patientsAmbiguous += 1
          errors.push(`${patientLine}: nenhum exame informado.`)
          continue
        }

        const lookupKey = getPatientLookupKey(batchEntry.paciente)
        let resolved = patientCache.get(lookupKey)
        if (!resolved) {
          try {
            resolved = await resolvePatientForImport(batchEntry.paciente, userId)
          } catch (error) {
            resolved = {
              kind: 'ambiguous',
              message: error instanceof Error ? error.message : 'falha ao resolver paciente.',
            }
          }
          patientCache.set(lookupKey, resolved)
        }

        if (resolved.kind !== 'resolved') {
          patientsAmbiguous += 1
          examsInvalid += batchEntry.exames.length
          errors.push(`${patientLine}: ${resolved.message}`)
          continue
        }

        if (!countedPatientIds.has(resolved.patient.id)) {
          countedPatientIds.add(resolved.patient.id)
          if (resolved.created) {
            patientsCreated += 1
          } else {
            patientsMatched += 1
          }
        }

        for (let examIndex = 0; examIndex < batchEntry.exames.length; examIndex += 1) {
          const exam = batchEntry.exames[examIndex]
          const examLine = `${patientLine} · Exame ${examIndex + 1}`
          if (!exam || typeof exam !== 'object') {
            examsInvalid += 1
            errors.push(`${examLine}: item inválido.`)
            continue
          }

          const examName = normalizeOptionalText(exam.tipo_exame)
          if (!examName) {
            examsInvalid += 1
            errors.push(`${examLine}: "tipo_exame" é obrigatório.`)
            continue
          }

          const rawValue = exam.resultado
          if (rawValue === undefined || rawValue === null || rawValue === '') {
            examsInvalid += 1
            errors.push(`${examLine}: "resultado" é obrigatório.`)
            continue
          }

          if (typeof rawValue !== 'number' && typeof rawValue !== 'string') {
            examsInvalid += 1
            errors.push(`${examLine}: "resultado" deve ser texto ou número.`)
            continue
          }

          const date = normalizeExamDate(exam.data_exame)
          if (!date) {
            examsInvalid += 1
            errors.push(`${examLine}: data inválida em "data_exame".`)
            continue
          }

          const { numericValue, textValue } = toExamValueColumns(rawValue)
          if (numericValue === null && !textValue) {
            examsInvalid += 1
            errors.push(`${examLine}: valor do exame inválido.`)
            continue
          }

          const unit = normalizeOptionalText(exam.unidade) ?? null
          const signature = buildExamValueSignature({ numericValue, textValue, unit })
          const normalizedExamName = normalizeIdentity(examName)

          let canonicalNormalized = normalizedExamName
          let canonicalDisplay = examName
          try {
            const resolvedType = await resolveExamTypeToMain(userId, normalizedExamName)
            if (resolvedType) {
              canonicalNormalized = resolvedType.mainNormalized
              canonicalDisplay = resolvedType.mainDisplay ?? examName
            }
          } catch {
            // Mantém nome original se não conseguir resolver
          }

          const examCacheKey = `${resolved.patient.id}::${canonicalNormalized}::${date}`

          let signatureSet = examSignaturesByKey.get(examCacheKey)
          if (!signatureSet) {
            const { data: existingRows, error: existingError } = await supabase
              .from('exam_results')
              .select('id, numeric_value, text_value, unit')
              .eq('patient_id', resolved.patient.id)
              .eq('normalized_exam_name', canonicalNormalized)
              .eq('date', date)
              .limit(200)

            if (existingError) {
              examsInvalid += 1
              errors.push(`${examLine}: não foi possível validar duplicidade.`)
              continue
            }

            signatureSet = new Set(
              (existingRows ?? []).map((row) =>
                buildExamValueSignature({
                  numericValue: row.numeric_value,
                  textValue: row.text_value,
                  unit: row.unit,
                }),
              ),
            )
            examSignaturesByKey.set(examCacheKey, signatureSet)
          }

          if (signatureSet.has(signature)) {
            examsAlreadyExisted += 1
            continue
          }

          const { data: inserted, error: insertError } = await supabase
            .from('exam_results')
            .insert({
              patient_id: resolved.patient.id,
              created_by_user_id: userId,
              exam_name: canonicalDisplay,
              date,
              numeric_value: numericValue,
              text_value: textValue,
              unit,
              laboratory: normalizeOptionalText(exam.laboratorio) ?? null,
              notes: normalizeOptionalText(exam.observacoes) ?? null,
              ref_min: parseNumberOrNull(exam.ref_min),
              ref_max: parseNumberOrNull(exam.ref_max),
              warning_threshold_percent: parseNumberOrNull(exam.limiar_alerta_percent),
              visibility: 'care_team',
            })
            .select('id, numeric_value, text_value, unit')
            .single()

          if (insertError) {
            const message = insertError.message.toLowerCase()
            if (insertError.code === '23505' || message.includes('duplicate')) {
              examsAlreadyExisted += 1
              signatureSet.add(signature)
              continue
            }
            examsInvalid += 1
            errors.push(`${examLine}: ${toRepositoryError(insertError, 'Falha ao importar exame.').message}`)
            continue
          }

          examsImported += 1
          const persisted = inserted as ExamRow
          signatureSet.add(
            buildExamValueSignature({
              numericValue: persisted.numeric_value,
              textValue: persisted.text_value,
              unit: persisted.unit,
            }),
          )
        }
      }

      return {
        examsImported,
        examsAlreadyExisted,
        examsInvalid,
        patientsCreated,
        patientsMatched,
        patientsAmbiguous,
        errors: errors.length ? errors : undefined,
      }
    } catch (error) {
      const lockErr = error as { isAcquireTimeout?: boolean }
      if (lockErr?.isAcquireTimeout) {
        throw new AppError(
          'A sessão está ocupada. Feche outras abas, aguarde alguns segundos e tente novamente.',
          { status: 503, code: 'UNKNOWN', cause: error },
        )
      }
      throw toRepositoryError(error, 'Não foi possível importar exames.')
    }
  },
}
